import { cn } from "@/lib/utils";
import { StandardButton } from "@/components/common/StandardButton";
import { ModalHeader } from "@/components/common/ModalHeader";
import { Input } from "@/shadcn/components/ui/input";
import { Textarea } from "@/shadcn/components/ui/textarea";
import {
  applyReviewEditToVenueCache,
  useAuth,
  useI18N,
  useEditVenueReview,
  usePostVenueReview,
  useModalHeight,
} from "common/hooks";
import { useQueryClient } from "@tanstack/react-query";
import { Storage } from "common/storage";
import { bucketUrlPrefix } from "common/config";
import { useEffect, useRef, useState } from "react";
import { readFile } from "@/helpers/cropImage";
import Zoom from "react-medium-image-zoom";
import { AddPhotoImageData, ReviewPhoto, VenueReview } from "common/types";
import Modal from "react-responsive-modal";
import { useDarkMode } from "@/hooks/useDarkMode";
import Loader from "@/components/common/Loader";
import { trackEvent } from "common/analytics";
import { deleteReviewImage, PhotoGuardrailError } from "common/apis";
import { useCustomToast } from "@/hooks/useCustomToast";
import { useGuardrailImageFeedback } from "@/hooks/useGuardrailImageFeedback";
import WideButton from "@/components/common/WideButton";


type AddReviewModalProps = {
  venueId: string;
  venueName: string;
  isOpen: boolean;
  // isSuccess distinguishes a post-submit close (skip share_experience_form_closed —
  // experience_shared already fired) from a user dismiss (X/backdrop/Esc → fire _closed).
  onClose: (isSuccess?: boolean) => void;
  venueDetails: any;
  onSuccess?: () => void;
  /** DEV-544 — when set, the modal edits this experience instead of creating one. */
  review?: VenueReview | null;
};

export function AddReviewModal({
  venueId,
  venueName,
  isOpen,
  onClose,
  venueDetails,
  onSuccess,
  review,
}: AddReviewModalProps) {
  const i18n = useI18N();
  const { modalStyle, modalClassName } = useModalHeight();
  const showToast = useCustomToast();
  const queryClient = useQueryClient();

  const isEditing = !!review;

  const [accessibilityExperience, setAccessibilityExperience] = useState<
    "cool" | "weird" | ""
  >("");
  const isDark = useDarkMode();
  const [note, setNote] = useState("");
  const { user } = useAuth();
  const [attachedImages, setAttachedImages] = useState<AddPhotoImageData[]>([]);
  // Photos already on the review, kept apart from `attachedImages`: submit() turns
  // every attached image into a blob and uploads it, so mixing the existing ones in
  // would silently re-upload them as duplicates. Removals are staged here and only
  // applied once the save itself has succeeded.
  const [existingPhotos, setExistingPhotos] = useState<ReviewPhoto[]>([]);
  const [removedPhotoIds, setRemovedPhotoIds] = useState<number[]>([]);
  const [isSavingEdit, setIsSavingEdit] = useState(false);
  const [errors, setErrors] = useState({
    note: false,
    accessibilityExperience: false,
    images: attachedImages.map(() => ({ note: false, selectedOption: false })),
  });
  const { mutate, data, error, status } = usePostVenueReview(true);
  const {
    mutateAsync: saveEdit,
    data: editData,
    error: editError,
  } = useEditVenueReview(true);
  const topRef = useRef(null);
  const noteRef = useRef(null);
  const imageRefs = useRef([]);

  const guardrail = useGuardrailImageFeedback(data, error, {
    savedLabel: i18n.get("photoGuardrailReviewSaved"),
  });

  // The edit mutation runs through mutateAsync, but its data/error still land here,
  // so guardrail outcomes on an edit are reported exactly as they are on a create:
  // "<saved> — we couldn't save one photo", with the reason logged, never shown.
  // Labelled for an edit — the create label ("Success! All saved") contradicts itself
  // when the whole point of the toast is that a photo wasn't saved.
  const editGuardrail = useGuardrailImageFeedback(editData, editError, {
    savedLabel: i18n.get("experienceUpdated"),
  });

  useEffect(() => {
    if (!editError || !(editError instanceof PhotoGuardrailError)) return;
    if (editGuardrail.rejectedInputIndexes.length) {
      setAttachedImages((prev) =>
        prev.filter(
          (_, i) => !editGuardrail.rejectedInputIndexes.includes(i),
        ),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editError]);

  useEffect(() => {
    if (!error) return;
    if (error instanceof PhotoGuardrailError) {
      if (guardrail.rejectedInputIndexes.length) {
        setAttachedImages((prev) =>
          prev.filter((_, i) => !guardrail.rejectedInputIndexes.includes(i)),
        );
      }
      return;
    }
    showToast(error.message, "error");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error, isDark]);

  useEffect(() => {
    if (data) {
      trackEvent("experience_shared", {
        brand_id: venueDetails?.brandId != null ? String(venueDetails?.brandId) : "",
        brand_name: venueDetails?.brandName ?? "",
        business_id: venueDetails.id.toString(),
        photos_count: attachedImages.length,
        place_name: venueDetails.name,
        rating: accessibilityExperience,
      });
      if (!guardrail.isPartial) {
        showToast(i18n.get("reviewSavedSuccessfully"), "success");
      }
      setAttachedImages([]);
      setNote("");
      setAccessibilityExperience("");
      setErrors({
        note: false,
        accessibilityExperience: false,
        images: [],
      });
      onSuccess?.();
      onClose(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, isDark]);

  // Seed (or clear) the form each time the modal opens, so an edit arrives pre-filled
  // and a subsequent create doesn't inherit what the edit left behind.
  useEffect(() => {
    if (!isOpen) return;
    setNote(review?.notes ?? "");
    setAccessibilityExperience(
      (review?.accessibility_experience as "cool" | "weird") ?? "",
    );
    setExistingPhotos(review?.reviewPhotos ?? []);
    setRemovedPhotoIds([]);
    setAttachedImages([]);
    setErrors({ note: false, accessibilityExperience: false, images: [] });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, review?.id]);

  const validateFields = () => {
    const newErrors = {
      note: note.trim().length < 5,
      accessibilityExperience: !accessibilityExperience,
      images: attachedImages.map(() => ({
        note: false,
        selectedOption: false,
      })),
    };

    setErrors(newErrors);

    const isAccessibilityExperienceValid = !newErrors.accessibilityExperience;
    const isNoteValid = !newErrors.note;

    if (!isAccessibilityExperienceValid) {
      topRef.current.scrollIntoView({ behavior: "smooth" });
    } else if (!isNoteValid) {
      noteRef.current.scrollIntoView({ behavior: "smooth" });
    }

    return isAccessibilityExperienceValid && isNoteValid;
  };

  const submitEdit = async () => {
    if (!validateFields()) return;
    setIsSavingEdit(true);
    try {
      // Edit first, photo removals second: a guardrail rejection on a newly added
      // photo fails the whole call, and this way round nothing the user already had
      // is destroyed by a save that didn't go through.
      const result = await saveEdit({
        reviewId: review.id.toString(),
        review: note.trim(),
        images: attachedImages,
        accessibilityExperience,
      });

      // The raw API rather than useDeletePhotoMutation: that hook invalidates the
      // venue query, and the refetch it starts returns the backend's not-yet-rebuilt
      // cache with the photo still attached, undoing the patch above.
      const deletedPhotoIds: number[] = [];
      try {
        for (const reviewPhotoId of removedPhotoIds) {
          const response = await deleteReviewImage(
            review.id.toString(),
            reviewPhotoId.toString(),
            Storage.getCurrentUserToken() || "",
          );
          if (!response || response.status === "error") {
            throw new Error(
              response?.message ?? i18n.get("couldNotDeleteExperiencePhoto"),
            );
          }
          deletedPhotoIds.push(reviewPhotoId);
        }
      } finally {
        // In `finally` so a delete that fails part-way still writes back the photos
        // that did go: the response listed them as they stood before any deletion,
        // and leaving it unpatched would show photos the server no longer has.
        if (deletedPhotoIds.length) {
          applyReviewEditToVenueCache(queryClient, {
            reviewId: review.id,
            notes: note.trim(),
            accessibilityExperience,
            reviewPhotos: result.reviewPhotos.filter(
              (photo) => !deletedPhotoIds.includes(photo.id),
            ),
          });
        }
      }

      // Read off the result, not editGuardrail: that derives from state which has
      // not re-rendered yet at this point. A partial rejection already draws its own
      // "saved — N photos weren't attached" toast, so don't stack a second one.
      if (!result.rejectedPhotos.length) {
        showToast(i18n.get("experienceUpdated"), "success");
      }
      onSuccess?.();
      onClose(true);
    } catch (e: any) {
      // A guardrail rejection is already surfaced by useGuardrailImageFeedback.
      if (!(e instanceof PhotoGuardrailError)) {
        showToast(e?.message, "error");
      }
    } finally {
      setIsSavingEdit(false);
    }
  };

  const submit = async () => {
    if (isEditing) {
      await submitEdit();
      return;
    }
    if (!validateFields()) {
      return;
    }
    if (venueId && user) {
      if (topRef?.current) {
        topRef.current.scrollIntoView({ behavior: "smooth" });
      }

      mutate({
        images: attachedImages,
        venueId: venueId,
        review: note.trim(),
        accessibilityExperience,
      });
    } else {
      showToast(i18n.get("mustBeLoggedInToSubmitReview"), "error");
    }
  };

  const onFileChange = async (e) => {
    const newImages = Array.from(e.target.files).map(async (file) => {
      const imageDataUrl = await readFile(file);
      return {
        imageUrl: imageDataUrl,
        note: "",
        selectedOption: undefined,
        key: undefined,
      };
    });
    const imgs = await Promise.all(newImages);
    setAttachedImages((prev) => [...prev, ...imgs]);
    setErrors((prev) => ({
      ...prev,
      images: [
        ...prev.images,
        ...imgs.map(() => ({ note: false, selectedOption: false })),
      ],
    }));
  };

  const handleExperiencePress = (value: "cool" | "weird") => {
    setAccessibilityExperience(accessibilityExperience === value ? "" : value);
    setErrors((prev) => ({ ...prev, accessibilityExperience: false }));
  };

  const firstFocusRef = useRef(null); // Reference for the element we need to set focus

  // Focus on the element when the component mounts
  useEffect(() => {
    if (firstFocusRef.current) {
      firstFocusRef.current.focus();
    }
  }, []);

  return (
    <Modal
      focusTrapped
      center
      blockScroll={true}
      animationDuration={0}
      open={isOpen}
      onClose={() => onClose()}
      classNames={{
        modal: "bg-[transparent] p-0 focus:outline-0 shadow-none md:w-[480px]",
        closeButton: "hidden",
      }}
    >
      <section
        ref={topRef}
        style={modalStyle}
        className={cn(modalClassName)}
        role="none"
      >
        {(status === "pending" || isSavingEdit) && <Loader />}

        <ModalHeader
          title={
            isEditing
              ? i18n.get("editExperience")
              : i18n.get("shareYourExperienceShort")
          }
          onClose={() => {
            setAttachedImages([]);
            setNote("");
            setAccessibilityExperience("");
            onClose();
          }}
        />

        <div
          aria-label="Modal Content"
          role="none"
          className="flex-grow overflow-y-auto"
        >
          <div className="self-stretch pt-2 flex-col justify-start items-center gap-4 flex">
            <div className="self-stretch h-30 pt-2 flex-col justify-start items-start gap-1 flex">
              <h1 className="self-stretch text-fg-neutral-strong label-m-medium px-6">
                {i18n.get("tellUsAboutYourExperienceHereAt")}
              </h1>

              <h2 className="self-stretch text-fg-brand-subtle label-m-medium px-6">
                {venueName}
              </h2>

              <div className="border w-full border-border-token-brand-minimal mt-2 h-[1px]"></div>
            </div>

            <div className="flex flex-col items-start gap-4 self-stretch">
              <h1 className="self-stretch text-fg-neutral-strong label-m-medium px-6">
                {i18n.get("howWasThisPlaceAboutYourAccessibilityNeeds")}
              </h1>

              <div className="flex items-start gap-4 w-full px-6">
                {/* Option 1 */}
                <button
                  type="button"
                  data-type="Like a boss"
                  onClick={() => handleExperiencePress("cool")}
                  
                  className={cn(
                    "w-full h-11 min-w-11 min-h-11 px-4 py-2 overflow-hidden rounded-lg ring-1 ring-inset flex items-center justify-center gap-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#19165C]/40 transition-colors",
                    accessibilityExperience === "cool"
                      ? "bg-bg-brand-tertiary-default ring-border-token-brand-subtle hover:bg-bg-brand-tertiary-hover"
                      : "bg-bg-brand-secondary-default ring-border-token-brand-minimal hover:bg-bg-brand-secondary-hover",
                  )}
                >
                  <img
                    src="/images/Orange.svg"
                    alt=""
                    className="w-6 h-6"
                    aria-hidden="true"
                  />
                  <span
                    className={cn(
                      "text-center flex flex-col justify-center label-m-semibold leading-5",
                    )}
                  >
                    {i18n.get("sweet")}
                  </span>
                </button>

                {/* Option 2 */}
                <button
                  type="button"
                  onClick={() => handleExperiencePress("weird")}
                  className={cn(
                    "w-full h-11 min-w-11 min-h-11 px-4 py-2 overflow-hidden rounded-lg ring-1 ring-inset flex items-center justify-center gap-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#19165C]/40 transition-colors",
                    accessibilityExperience === "weird"
                    ? "bg-bg-brand-tertiary-default ring-border-token-brand-subtle hover:bg-bg-brand-tertiary-hover"
                    : "bg-bg-brand-secondary-default ring-border-token-brand-minimal hover:bg-bg-brand-secondary-hover",
                  )}
                >
                  <img
                    src="/images/OrangeSlice.svg"
                    alt=""
                    className="w-6 h-6"
                    aria-hidden="true"
                  />
                  <span
                    className={cn(
                      "text-center flex flex-col justify-center label-m-semibold leading-5",
                    )}
                  >
                    {i18n.get("sour")}
                  </span>
                </button>
              </div>

              {errors.accessibilityExperience && (
                <div className="text-red-600 label-s-regular px-6">
                  {i18n.get("pleaseSelectYourAccessibilityExperience") ||
                    "Please select your accessibility experience (Sweet or Sour)"}
                </div>
              )}
            </div>

            <div className="border w-full border-border-token-brand-minimal mt-2 h-[1px]"></div>

            <div className="w-full flex-col justify-center items-start gap-4 flex px-6">
              <h1 className="self-stretch text-fg-neutral-strong label-m-medium">
                {i18n.get("tellUsMoreAboutYourExperience")}
              </h1>

              <div className=" w-full">
                <Textarea
                  id="review"
                  placeholder={i18n.get("describeYourExperienceInMoreDetail")}
                  className="scrollbar-thin h-28"
                  value={note}
                  aria-describedby="charCount"
                  onChange={(e) => {
                    setNote(e.target.value);
                  }}
                  maxLength={500}
                  ref={noteRef}
                  error={note.length > 0 && note.trim().length < 5}
                />
                <div
                  id="charCount"
                  className={`label-m-medium mt-3 ${
                    note.length > 0 && note.trim().length < 5
                      ? "text-fg-error-subtle"
                      : "text-fg-neutral-subtle"
                  }`}
                >
                  {note.length > 0 && note.trim().length < 5
                    ? i18n.get("pleaseEnterAtLeast5Characters")
                    : `${note.trim().length}/500 characters`}
                </div>
              </div>
            </div>

            <div className="w-full flex-col justify-center items-start gap-1 px-6 flex">
              <div className="grid gap-4 w-full">
                {/* Already on the review. Removing one stages it — nothing is deleted
                    until Save succeeds. */}
                {existingPhotos
                  .filter((photo) => !removedPhotoIds.includes(photo.id))
                  .map((photo) => (
                    <div key={`existing-${photo.id}`} className="grid gap-2">
                      <div className="relative w-full h-auto">
                        <Zoom>
                          <img
                            src={bucketUrlPrefix + (photo.path || photo.pathThumb)}
                            alt={photo.altText || "Experience photo"}
                            className="w-full h-[300px] object-cover rounded-[10px]"
                          />
                        </Zoom>
                        <StandardButton
                          onClick={() =>
                            setRemovedPhotoIds((ids) => [...ids, photo.id])
                          }
                          variant="removeImage"
                          position="absolute"
                          className="top-2 right-3 m-2"
                          imageSrc="/images/Close.svg"
                          imageAlt="Remove image"
                          imageContainer={true}
                          imageContainerClassName="w-6 h-6 p-1 bg-[#f9f8fd] rounded border border-[#cbc9f2] justify-center items-center inline-flex"
                          aria-label={`Remove photo ${photo.altText || ""}`.trim()}
                        />
                      </div>
                    </div>
                  ))}
                {attachedImages.map((image, index) => (
                  <div
                    key={index}
                    className="grid gap-2 focus-blue"
                    tabIndex={0}
                  >
                    <div className="relative w-full h-auto">
                      <Zoom>
                        <img
                          src={image.imageUrl}
                          alt="attached image"
                          className="w-full h-[300px] object-cover rounded-[10px]"
                        />
                      </Zoom>
                      <StandardButton
                        onClick={() => {
                          setAttachedImages(
                            attachedImages.filter(
                              (_, imgIndex) => imgIndex !== index,
                            ),
                          );
                        }}
                        variant="removeImage"
                        position="absolute"
                        className="top-2 right-3 m-2"
                        imageSrc="/images/Close.svg"
                        imageAlt="Remove image"
                        imageContainer={true}
                        imageContainerClassName="w-6 h-6 p-1 bg-[#f9f8fd] rounded border border-[#cbc9f2] justify-center items-center inline-flex"
                        aria-label={`Remove attached image ${index + 1}`}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <footer className="py-3 min-h-14 flex items-center justify-center text-center px-6 border-b border-border-token-brand-minimal sticky bottom-0 bg-bg-brand-secondary-default z-1000">
          <div className="flex-col md:flex-row justify-end items-center gap-4 flex w-full">
            <div className="w-full justify-start items-center gap-4 inline-flex">
              <label
                htmlFor="picture" // Wrap the entire div with the label
                className="grow shrink basis-0 h-12 px-6 py-3 bg-container-brand-minimal rounded-xl border border-[#cbc9f2] justify-center items-center gap-3 flex cursor-pointer focus-blue"
                aria-label="Add photos"
                tabIndex={0}
                role="button"
              >
                <div className="text-center text-fg-neutral-strong title-s-semibold w-full">
                  {i18n.get("addPhotos")}
                </div>
                <Input
                  id="picture"
                  type="file"
                  accept="image/*"
                  multiple
                  className="hidden"
                  onChange={onFileChange}
                />
              </label>
            </div>

            <div className="w-full justify-center items-center inline-flex">
              <WideButton
                className="grow"
                disabled={note.trim().length < 5}
                onClick={() => {
                  submit();
                }}
                aria-label={isEditing ? "Save experience" : "Submit review"}
              >
                {isEditing ? i18n.get("save") : i18n.get("share")}
              </WideButton>
            </div>
          </div>
        </footer>
      </section>
    </Modal>
  );
}
